import {
  AirwallexEnv,
  CardNumberElementType,
  confirmPaymentIntent,
  CvcElementType,
  ExpiryDateElementType,
  getElement,
  Intent,
  loadAirwallex,
} from "airwallex-payment-elements";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import LoadingContainer from "@componentsV1/containers/LoadingContainer";
import LegalData from "@componentsV1/pages/Payment/molecules/LegalData";
import PriceType from "@componentsV1/pages/Payment/molecules/PriceType";
import RightSideBlock from "@componentsV1/pages/Payment/molecules/RightSideBlock";
import Button from "@componentsV1/shared/Button";
import { useLazyCreatePaymentIntentQuery } from "@store/services/PaymentService";
import { useLazyConfirmReservationQuery } from "@store/services/ReservationService";
import { showToast } from "@store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "@store/store";
import TOAST from "@utils/constants/toast";
import { prepareParams } from "@utils/helpers/rooms";

import { usePaymentState } from "./hooks/usePaymentState";
import styles from "./index.module.scss";

interface PaymentConfirmation extends Intent {
  authorization_code: string;
  payment_method_transaction_id: string;
  latest_payment_attempt: {
    authorization_code: string;
    payment_method_transaction_id: string;
    id: string;
    payment_intent_id: string;
    payment_method: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
}

function Payment() {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const [createIntent] = useLazyCreatePaymentIntentQuery();
  const [confirmReservation] = useLazyConfirmReservationQuery();
  const [searchParams] = useSearchParams();

  const { cruiseId } = useParams();

  const { isCruiseLoading } = useTypedSelector((state) => state.search);
  const { rooms } = useTypedSelector((state) => state.rooms);

  const source = searchParams.get("source") ?? "";

  const {
    terms_and_conditions,
    participant_release_agreement,
    supplemental_terms,
    show_cancellation_policy,
    guest_ticket_contract,
    liability_and_cancellation,
    airwallex_env,
  } = useTypedSelector((state) => state.environment);

  const {
    priceType,
    setPriceType,
    isLoading,
    setIsLoading,
    cardNumberReady,
    setCardNumberReady,
    cvcReady,
    setCvcReady,
    expiryReady,
    setExpiryReady,
    cardNumberComplete,
    setCardNumberComplete,
    cvcComplete,
    setCvcComplete,
    expiryComplete,
    setExpiryComplete,
    errorMessage,
    setErrorMessage,
    inputErrorMessage,
    setInputErrorMessage,
  } = usePaymentState();

  const cardNumberRef = useRef<CardNumberElementType | null>(null);
  const expiryRef = useRef<ExpiryDateElementType | null>(null);
  const cvcRef = useRef<CvcElementType | null>(null);

  const [legalData, setLegalData] = useState({
    terms_and_conditions: !terms_and_conditions,
    participant_release_agreement: !participant_release_agreement,
    supplemental_terms: !supplemental_terms,
    guest_ticket_contract: !guest_ticket_contract,
    show_cancellation_policy: !show_cancellation_policy,
    liability_and_cancellation: !liability_and_cancellation,
  });

  const transactions: [
    {
      full?: number;
      deposit?: number;
      amount: number;
      currency: string;
      transaction_type: string;
    },
  ] = useMemo(() => {
    const output = {
      full: 0,
      deposit: 0,
      amount: 0,
      currency: rooms?.[1].pricing?.payment_schedule?.[0].currency ?? "",
      transaction_type: "creditcard",
    };

    const deposit = Object.values(rooms ?? {}).reduce((sum, room) => {
      const roomDeposit = room?.pricing?.payment_schedule?.[0]?.amount ?? 0;

      return Number(sum) + Number(roomDeposit);
    }, 0);

    output.deposit = deposit;

    Object.values(rooms ?? {}).forEach((room) => {
      const paymentSchedule = room?.pricing?.payment_schedule;

      if (Array.isArray(paymentSchedule)) {
        paymentSchedule.forEach((payment) => {
          if (payment.amount) {
            output.full += parseFloat(payment.amount);
          }
        });
      }
    });

    if (priceType === "full") {
      output.amount = output.full;
    }

    if (priceType === "deposit") {
      output.amount = output.deposit;
    }

    output.amount = Math.ceil(output.amount * 100) / 100;
    output.full = Math.ceil(output.full * 100) / 100;
    output.deposit = Math.ceil(output.deposit * 100) / 100;

    return [output];
  }, [rooms, cardNumberRef.current, priceType]);

  const params = useMemo(() => {
    const totalRooms = +(searchParams.get("rooms") ?? 0);
    const currentRoomNumber = +(searchParams.get("room") ?? 0);

    const output = prepareParams(
      searchParams,
      rooms ?? {},
      currentRoomNumber,
      currentRoomNumber < totalRooms,
    );

    return output;
  }, [searchParams, rooms]);

  const handlePriceTypeChange = (type: typeof priceType) => setPriceType(type);

  const handleLegalDataChange = ({ value, valueKey }: { value: boolean; valueKey: string }) => {
    setLegalData((prev) => ({ ...prev, [valueKey]: value }));
  };

  const onPaymentFail = () => {
    dispatch(
      showToast({
        type: TOAST.ERROR_TYPE,
        message: t("Payment failed"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const onPaymentSuccess = () => {
    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: t("Payment success"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );

    navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setErrorMessage("");

    const cardNumElement = getElement("cardNumber");

    if (!cardNumElement) {
      setIsLoading(false);

      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: t("Card number element is not available"),
          duration: TOAST.DEFAULT_DURATION,
        }),
      );

      return;
    }

    try {
      const { amount, currency } = transactions[0];

      const { data } = await createIntent({
        pnr: rooms?.[1]?.pnr ?? "",
        transactions: [{ amount, currency }],
        source,
      });

      const paymentConfirmation = (await confirmPaymentIntent({
        element: cardNumElement,
        id: data?.id,
        client_secret: data?.client_secret,
        payment_method_options: {
          card: {
            auto_capture: true,
          },
        },
      })) as PaymentConfirmation;

      const intentTransactions = [
        {
          transaction_type: "creditcard" as const,
          made_on: paymentConfirmation.created_at,
          made_by: "Website" as const,
          amount: paymentConfirmation.amount,
          currency: paymentConfirmation.currency,
          card_clearance: {
            clearance_system: "airwallex" as const,
            auth_code: paymentConfirmation.latest_payment_attempt.authorization_code,
            transaction_id:
              paymentConfirmation.latest_payment_attempt.payment_method_transaction_id,
            other: {
              card_type: paymentConfirmation.latest_payment_attempt.payment_method.card.brand,
              last_four: paymentConfirmation.latest_payment_attempt.payment_method.card.last4,
              transaction_id: paymentConfirmation.latest_payment_attempt.id,
              intent_id: paymentConfirmation.latest_payment_attempt.payment_intent_id,
            },
          },
        },
      ];

      await confirmReservation({
        pnr: rooms?.[1]?.pnr ?? "",
        transactions: intentTransactions,
      });

      setIsLoading(false);
      onPaymentSuccess();
    } catch (error) {
      const errorText = error instanceof Error ? error.message : JSON.stringify(error);

      setIsLoading(false);

      onPaymentFail();
      setErrorMessage(errorText);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const initializeAirwallex = async () => {
      try {
        const airwallex = await loadAirwallex({
          env: airwallex_env as AirwallexEnv,
          origin: window.location.origin,
        });

        const cardNumEle = airwallex?.createElement("cardNumber");
        const cvcEle = airwallex?.createElement("cvc");
        const expiryEle = airwallex?.createElement("expiry");

        if (!cardNumEle || !cvcEle || !expiryEle) {
          return;
        }

        cardNumEle?.mount("cardNumber");
        cvcEle?.mount("cvc");
        expiryEle?.mount("expiry");
        cardNumberRef.current = cardNumEle;
        expiryRef.current = expiryEle;
        cvcRef.current = cvcEle;
      } catch (error) {
        dispatch(
          showToast({
            type: TOAST.ERROR_TYPE,
            message: t("Error initializing Airwallex"),
            duration: TOAST.DEFAULT_DURATION,
          }),
        );
      }
    };

    initializeAirwallex();

    const onReady = (event: { detail: { type: string } }) => {
      const { type } = event.detail;

      if (type === "cardNumber") {
        setCardNumberReady(true);
      }

      if (type === "cvc") {
        setCvcReady(true);
      }

      if (type === "expiry") {
        setExpiryReady(true);
      }
    };

    const onChange = (event: { detail: { type: string; complete: boolean } }) => {
      const { type, complete } = event.detail;

      if (type === "cardNumber") {
        setCardNumberComplete(complete);
      }

      if (type === "cvc") {
        setCvcComplete(complete);
      }

      if (type === "expiry") {
        setExpiryComplete(complete);
      }
    };

    const onFocus = (event: CustomEvent) => {
      const { type } = event.detail;

      setInputErrorMessage({
        ...inputErrorMessage,
        [type]: "",
      });
    };

    const onBlur = (event: CustomEvent) => {
      const { type, error } = event.detail;

      setInputErrorMessage((prev) => ({
        ...prev,
        [type]: error?.message ?? JSON.stringify(error),
      }));
    };

    const cardNumberElement = document.getElementById("cardNumber");
    const expiryElement = document.getElementById("expiry");
    const cvcElement = document.getElementById("cvc");
    const domElementArray = [cardNumberElement, expiryElement, cvcElement];

    domElementArray.forEach((element) => {
      element?.addEventListener("onReady", onReady as unknown as EventListener);
      element?.addEventListener("onChange", onChange as unknown as EventListener);
      element?.addEventListener("onFocus", onFocus as unknown as EventListener);
      element?.addEventListener("onBlur", onBlur as unknown as EventListener);
    });

    return () => {
      domElementArray.forEach((element) => {
        element?.removeEventListener("onReady", onReady as unknown as EventListener);
        element?.removeEventListener("onChange", onChange as unknown as EventListener);
        element?.removeEventListener("onFocus", onFocus as unknown as EventListener);
        element?.removeEventListener("onBlur", onBlur as unknown as EventListener);
      });
    };
  }, []);

  const allElementsReady = cardNumberReady && cvcReady && expiryReady;
  const allElementsComplete = cardNumberComplete && cvcComplete && expiryComplete;

  const legalDataAccepted =
    legalData.guest_ticket_contract &&
    legalData.participant_release_agreement &&
    legalData.show_cancellation_policy &&
    legalData.supplemental_terms &&
    legalData.terms_and_conditions &&
    legalData.liability_and_cancellation;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>
          <h1 className={styles.title}>{t("Payment")}</h1>
          <LoadingContainer isLoading={isCruiseLoading}>
            <PriceType
              full={transactions[0].full ?? 0}
              deposit={transactions[0].deposit ?? 0}
              currency={transactions[0].currency || "USD"}
              onPriceTypeChange={handlePriceTypeChange}
              priceType={priceType}
            />
          </LoadingContainer>
          <LoadingContainer isLoading={!allElementsReady} />
          <form className={styles.form} style={{ display: !allElementsReady ? "none" : "flex" }}>
            {errorMessage.length > 0 && <div className={styles.inputError}>{errorMessage}</div>}
            <div style={{ display: allElementsReady ? "block" : "none" }}>
              <div className={styles.fieldContainer}>
                <div className={styles.labelAirWallex}>Card number</div>
                <div className={styles.inputAirWallex} id="cardNumber" />
                <div className={styles.inputError}>{inputErrorMessage.cardNumber || " "}</div>
              </div>
              <div className={styles.fieldContainer}>
                <div className={styles.labelAirWallex}>Expiry</div>
                <div className={styles.inputAirWallex} id="expiry" />
                <span className={styles.inputError}>{inputErrorMessage.expiry || " "}</span>
              </div>
              <div className={styles.fieldContainer}>
                <div className={styles.labelAirWallex}>Card Code</div>
                <div className={styles.inputAirWallex} id="cvc" />
                <span className={styles.inputError}>{inputErrorMessage.cvc || " "}</span>
              </div>
            </div>

            <LegalData
              terms_and_conditions_checked={legalData.terms_and_conditions}
              participant_release_agreement_checked={legalData.participant_release_agreement}
              supplemental_terms_checked={legalData.supplemental_terms}
              guest_ticket_contract_checked={legalData.guest_ticket_contract}
              show_cancellation_policy_checked={legalData.show_cancellation_policy}
              liability_and_cancellation_checked={legalData.liability_and_cancellation}
              onChange={handleLegalDataChange}
            />
          </form>

          <div className={styles.inputs}>
            <Button
              label={isLoading ? t("Loading") : t("Pay")}
              className={styles.button}
              onClick={handleConfirm}
              loading={isLoading}
              disabled={!allElementsComplete || isLoading || !legalDataAccepted}
            />
          </div>
        </div>
        <RightSideBlock />
      </div>
    </div>
  );
}

export default Payment;
